/**
 * What the Outbound Dashboard draws, and the arithmetic it does on it.
 *
 * Live inputs: the four statuses of the activation listing (`pending`,
 * `assigned`, `claimed`, `completed`), the routes the unfinished ones pick from,
 * and open selector sessions. Figures derived from them are computed here
 * rather than in a component. Everything else on the screen is
 * `DashboardSample` - see `dashboard-mock.ts` for why.
 */

/**
 * `TaskActivationSummary.status`, counted. The first three are the activated and
 * unfinished work, whenever it was activated; `completed` is only what finished
 * since the shift began, so a shift's completion starts from zero.
 */
export interface TaskCounts {
  /** Pooled: live, nobody holds it. */
  pending: number;
  /** Held by a selector, not yet claimed. */
  assigned: number;
  /** Committed to by the selector, not yet finished. */
  claimed: number;
  /** Completed since the shift began. */
  completed: number;
}

export interface TaskProgress {
  total: number;
  completed: number;
  remaining: number;
  /** Whole percent, 0 when there is no work. */
  percent: number;
  /** Claimed by a selector and not finished: `claimed`. */
  inProgress: number;
  /** Not yet claimed: `pending` + `assigned`. */
  waiting: number;
}

/** A whole percent, rounded down so 99.6% never reads as done. */
export function percentOf(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.floor((part / whole) * 100);
}

export function taskProgress(counts: TaskCounts): TaskProgress {
  const total =
    counts.pending + counts.assigned + counts.claimed + counts.completed;

  return {
    total,
    completed: counts.completed,
    remaining: total - counts.completed,
    percent: percentOf(counts.completed, total),
    inProgress: counts.claimed,
    waiting: counts.pending + counts.assigned,
  };
}

export interface UnitProgress {
  picked: number;
  total: number;
}

/** One claimed task a selector is working: a row of the pickers drawer. */
export interface PickingTask {
  selectorId: string;
  /** The warehouse-scoped number a person reads (`task_internal_id`). */
  taskNumber: number;
  zones: ReadonlyArray<string>;
  /** Pick lines finished, of `total`. */
  done: number;
  total: number;
}

/** Who is on shift, split the way the Pickers card counts them. */
export interface PickersDetail {
  /** Claimed tasks held by active selectors, lowest number first. */
  picking: ReadonlyArray<PickingTask>;
  /** Active selectors holding no claimed task. */
  waiting: ReadonlyArray<string>;
  /** Selectors with an open session and no device action for `IDLE_AFTER_MS`. */
  idle: ReadonlyArray<string>;
}

export interface NotActivated {
  routes: number;
  tasks: number;
  /** Both are floors: there are more plans than one page of them. */
  capped: boolean;
}

/** The slice of a `TaskPlanSummary` the not-activated card reads. */
export interface PlanRow {
  routes: ReadonlyArray<{ id: string }>;
  total_tasks: number;
}

/**
 * What releasing would put on the floor: the routes the unactivated plans cover
 * (a route shared by two plans counts once) and the tasks they hold. A plan still
 * being planned, or one that failed, has no routes and no tasks yet, so it adds
 * nothing to either figure. `hasMore` is the listing's own word that this page
 * was not all of it.
 */
export function notActivated(
  plans: ReadonlyArray<PlanRow>,
  hasMore: boolean,
): NotActivated {
  const routes = new Set(plans.flatMap((p) => p.routes.map((r) => r.id)));

  return {
    routes: routes.size,
    tasks: plans.reduce((sum, p) => sum + p.total_tasks, 0),
    capped: hasMore,
  };
}

export interface Pickers {
  active: number;
  idle: number;
}

/** A read capped at one page: `capped` says there was more than it counted. */
export interface Counted {
  count: number;
  capped: boolean;
}

/**
 * Distinct routes among the activations given (by route id, so one route picked
 * by many tasks counts once). `capped` is true when the page was full, meaning
 * the listing held more rows than were read and the count is a floor.
 */
export function distinctRoutes(
  rows: ReadonlyArray<{ routes: ReadonlyArray<{ id: string }> }>,
  pageLimit: number,
): Counted {
  const ids = new Set(rows.flatMap((r) => r.routes.map((route) => route.id)));

  return { count: ids.size, capped: rows.length >= pageLimit };
}

/** The slice of a configured shift this screen reads (system controls owns it). */
export interface ShiftWindow {
  name: string;
  /** Minutes since local midnight, `[0, 1440)`. */
  startMinute: number;
  /** Minutes since local midnight; past 1440 for a shift that crosses midnight. */
  endMinute: number;
}

/** Minutes since midnight at `at` (epoch ms) in an IANA zone; undefined if the zone is not one. */
export function minuteOfDayIn(
  timezone: string,
  at: number,
): number | undefined {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      hour: "numeric",
      minute: "numeric",
    }).formatToParts(at);
    const part = (type: string) =>
      Number(parts.find((p) => p.type === type)?.value ?? 0);

    return part("hour") * 60 + part("minute");
  } catch {
    return undefined;
  }
}

/**
 * The configured shift running at `at` in the warehouse's zone, if any. A shift
 * that crosses midnight (Night: 22:00-06:00 is stored as 1320-1800) is running in
 * its tail too, so the minute is also tried a day on. The first match wins when
 * shifts overlap; gaps between shifts have none.
 */
export function currentShift<S extends ShiftWindow>(
  shifts: ReadonlyArray<S>,
  timezone: string,
  at: number,
): S | undefined {
  const minute = minuteOfDayIn(timezone, at);
  if (minute === undefined) return undefined;

  return shifts.find(
    (s) =>
      (minute >= s.startMinute && minute < s.endMinute) ||
      (minute + 1440 >= s.startMinute && minute + 1440 < s.endMinute),
  );
}

const MINUTE_MS = 60_000;

/**
 * When `shift` (running at `at`) began, epoch ms. Inside the tail of a shift that
 * crossed midnight the start was yesterday, so the elapsed time wraps. Whole
 * minutes, so it holds steady for the whole shift instead of drifting with `at`.
 */
export function shiftStartedAt(
  shift: ShiftWindow,
  timezone: string,
  at: number,
): number | undefined {
  const minute = minuteOfDayIn(timezone, at);
  if (minute === undefined) return undefined;

  const elapsed =
    minute >= shift.startMinute
      ? minute - shift.startMinute
      : minute + 1440 - shift.startMinute;

  return Math.floor(at / MINUTE_MS) * MINUTE_MS - elapsed * MINUTE_MS;
}

/**
 * Midnight in the warehouse's zone, the stand-in for "the start of the shift"
 * when no shift is running (a gap, none configured). Falls back to the browser's
 * midnight if the zone is not one.
 */
export function dayStartedAt(timezone: string, at: number): number {
  const minute = minuteOfDayIn(timezone, at);
  if (minute === undefined) return new Date(at).setHours(0, 0, 0, 0);

  return Math.floor(at / MINUTE_MS) * MINUTE_MS - minute * MINUTE_MS;
}

/** The slice of a claimed `TaskActivationSummary` the pickers drawer reads. */
export interface ClaimedRow {
  selector_id?: string | null;
  task_internal_id: number;
  zones: ReadonlyArray<{ zone_name: string }>;
  task_detail_count: number;
  task_detail_fulfillment_count: number;
}

/**
 * How long a device may go without recording an action before its selector is
 * idle. This is the definition printed on the Pickers card.
 */
export const IDLE_AFTER_MS = 10 * MINUTE_MS;

/** The slice of an open `SelectorSession` the pickers figures read. */
export interface SessionRow {
  selector_id: string;
  /** When the service last accepted a device action for the session. */
  last_device_action_at: string;
}

/**
 * Selectors with an open session, split by device activity: active if any of
 * their sessions recorded an action within `IDLE_AFTER_MS` of `now`, idle
 * otherwise. A timestamp that does not parse is no evidence of activity, so it
 * counts as idle.
 */
function splitBySessionActivity(
  sessions: ReadonlyArray<SessionRow>,
  now: number,
): { active: Set<string>; idle: Set<string> } {
  const lastAction = new Map<string, number>();

  for (const { selector_id, last_device_action_at } of sessions) {
    const at = Date.parse(last_device_action_at);
    const seen = lastAction.get(selector_id);

    if (seen === undefined || at > seen || Number.isNaN(seen)) {
      lastAction.set(selector_id, at);
    }
  }

  const active = new Set<string>();
  const idle = new Set<string>();

  for (const [id, at] of lastAction) {
    (now - at < IDLE_AFTER_MS ? active : idle).add(id);
  }

  return { active, idle };
}

/**
 * Who is picking what: the claimed tasks of active selectors, the active
 * selectors holding none, and the idle ones. The same split `pickerCounts`
 * counts, with the identities kept. A selector holding two claimed tasks has two
 * rows (so the rows can outnumber `pickerCounts().active`, which counts
 * selectors); an idle selector's claimed tasks are not listed, and a claimed task
 * whose selector has no open session is not on the floor and is left out.
 */
export function pickersDetail(
  sessions: ReadonlyArray<SessionRow>,
  claimed: ReadonlyArray<ClaimedRow>,
  now: number,
): PickersDetail {
  const { active, idle } = splitBySessionActivity(sessions, now);
  const picking = claimed
    .filter((row) => row.selector_id != null && active.has(row.selector_id))
    .map((row) => ({
      selectorId: row.selector_id as string,
      taskNumber: row.task_internal_id,
      zones: row.zones.map((zone) => zone.zone_name),
      done: row.task_detail_fulfillment_count,
      total: row.task_detail_count,
    }))
    .sort((a, b) => a.taskNumber - b.taskNumber);
  const working = new Set(picking.map((task) => task.selectorId));

  return {
    picking,
    waiting: [...active].filter((id) => !working.has(id)),
    idle: [...idle],
  };
}

/**
 * Open selector sessions, split by device activity: idle is no device action for
 * `IDLE_AFTER_MS`, active is anything more recent. Holding a claimed task does
 * not make a selector active, and holding none does not make one idle.
 */
export function pickerCounts(
  sessions: ReadonlyArray<SessionRow>,
  now: number,
): Pickers {
  const { active, idle } = splitBySessionActivity(sessions, now);

  return { active: active.size, idle: idle.size };
}

export interface HourlyPieces {
  /** 24-hour clock hour, e.g. 8 for 8-9am. */
  hour: number;
  pieces: number;
  /** The hour still in progress. */
  partial?: boolean;
}

export interface ZoneProgress {
  /** The item service's zone id, which keys the row; 0 is the unzoned bucket. */
  id: number;
  /** The zone's name; empty for the unzoned bucket, which the screen names. */
  zone: string;
  picked: number;
  /** Units still expected: planned, less lines that came up short. */
  total: number;
  /** Picks whose item resolved to no zone at planning time. */
  unzoned?: boolean;
}

/** The slice of a stats zone this screen reads (`TaskActivationStatsZone`). */
export interface StatsZone {
  zone_ext_id: number;
  zone_name: string;
  planned: number;
  picked: number;
  shorted: number;
}

/**
 * A name with its first letter in capitals and the rest as given: zone names are
 * whatever the warehouse typed into the catalog, and a lowercase "cold" reads as
 * a mistake on a card whose neighbours are all capitalised. Only the first
 * character moves, so "FRZ" and "Dry Goods" stay as they are.
 */
export function capitalizeFirst(name: string): string {
  return name.charAt(0).toLocaleUpperCase() + name.slice(1);
}

/**
 * The service's zones as meters. Short lines leave the total, as they do in the
 * hero, so a zone whose remaining lines were all shorted reads done. The service
 * reserves id 0 for picks with no zone and names it in English; the screen names
 * it itself (`unzoned`), so only the id is read.
 */
export function zoneProgress(zones: ReadonlyArray<StatsZone>): ZoneProgress[] {
  return zones.map((z) =>
    z.zone_ext_id === 0
      ? {
          id: 0,
          zone: "",
          picked: z.picked,
          total: z.planned - z.shorted,
          unzoned: true,
        }
      : {
          id: z.zone_ext_id,
          zone: capitalizeFirst(z.zone_name),
          picked: z.picked,
          total: z.planned - z.shorted,
        },
  );
}

/**
 * Yassin's sort hierarchy: what blocks the floor first, what a person must do
 * next, and then what is only worth knowing. `actionable` is the first two;
 * `informational` is the third.
 */
export type ExceptionSeverity = "actionable" | "informational";

export type ExceptionKind = "suspended" | "manualValidation";

export interface ShiftException {
  id: string;
  kind: ExceptionKind;
  severity: ExceptionSeverity;
  /** Pre-formatted, as the design prints it: "Priya Cohen". */
  selector: string;
  taskId: string;
  /** What happened, in the design's words. */
  detail: string;
  /** Minutes since it happened, for the age and for ordering. */
  ageMinutes: number;
}

export type ExceptionFilter = ExceptionSeverity | "all";

/** What the exceptions list shows for a tab, oldest first within the tab. */
export function visibleExceptions(
  exceptions: ReadonlyArray<ShiftException>,
  filter: ExceptionFilter,
): ShiftException[] {
  return exceptions
    .filter((e) => filter === "all" || e.severity === filter)
    .sort((a, b) => b.ageMinutes - a.ageMinutes);
}

export function exceptionCounts(
  exceptions: ReadonlyArray<ShiftException>,
): Record<ExceptionFilter, number> {
  const actionable = exceptions.filter((e) => e.severity === "actionable");

  return {
    actionable: actionable.length,
    informational: exceptions.length - actionable.length,
    all: exceptions.length,
  };
}

/** `83` -> `"1h 23m ago"`, `52` -> `"52m ago"`. */
export function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
}

/** Average of the *completed* hours: a partial hour would drag it down. */
export function averagePerHour(hours: ReadonlyArray<HourlyPieces>): number {
  const done = hours.filter((h) => !h.partial);
  if (done.length === 0) return 0;
  return Math.round(done.reduce((sum, h) => sum + h.pieces, 0) / done.length);
}

/** `8` -> `"8a"`, `12` -> `"12p"`, `0` -> `"12a"`. */
export function hourLabel(hour: number): string {
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}${hour < 12 ? "a" : "p"}`;
}

export interface DashboardSample {
  exceptions: ReadonlyArray<ShiftException>;
}

export interface Dashboard {
  tasks: TaskProgress;
  /** Units across the same tasks as `tasks`, less lines that came up short. */
  units: UnitProgress;
  /**
   * Pieces confirmed per hour since `since`, oldest first, the hour in progress
   * last. Empty when the warehouse timezone is unknown (the read needs it).
   */
  hourly: ReadonlyArray<HourlyPieces>;
  /** When the shift (or, with none running, the day) began, epoch ms. */
  since: number;
  /** Distinct routes among the activated, unfinished tasks. */
  activatedRoutes: Counted;
  pickers: Pickers;
  /** Plans nobody has released yet, which is what the Activate buttons act on. */
  notActivated: NotActivated;
  /** Units by zone, over the same tasks as `units`. */
  zones: ReadonlyArray<ZoneProgress>;
  /** The same selectors by name of task, for the drawer behind the card. */
  pickersDetail: PickersDetail;
  /** The configured shift running now (system controls), if one is. */
  shift: ShiftWindow | undefined;
  /** When the live figures were last read, epoch ms. */
  updatedAt: number;
  /** Everything the service cannot answer yet. */
  sample: DashboardSample;
}
